import { useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { Image, Camera } from 'lucide-react';
import AttachmentMenu from '../../components/AttachmentMenu';
import { supabase } from '../../supabase/client';
import { generateImageUuid } from '../../utils/imageUrls';

/**
 * ProfileAttachmentMenu - Attachment menu for profile images
 * 
 * Wraps the base AttachmentMenu with profile-specific buttons:
 * - Picture (file upload - uploads immediately)
 * - Camera (capture - uploads immediately)
 * 
 * Profile images upload immediately (no staging area) and update the user's profile.
 */
const ProfileAttachmentMenu = forwardRef(({ onClose, onImageUploaded }, ref) => {
  const attachmentMenuRef = useRef(null);
  const userId = useSelector((s) => s.user.authUserId);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    triggerFileInput: () => attachmentMenuRef.current?.triggerFileInput(),
    triggerCamera: () => attachmentMenuRef.current?.triggerCamera()
  }));

  const handlePictureClick = () => {
    attachmentMenuRef.current?.triggerFileInput();
  };

  const handleCameraClick = () => {
    attachmentMenuRef.current?.triggerCamera();
  };

  const handleFileSelect = async (files, type, imageData) => {
    if (!userId) { return; }

    try {
      let file;
      
      if (type === 'camera') {
        file = Array.from(files)[0];
      } else if (type === 'image') {
        file = Array.from(files)[0];
      } else {
        return;
      }

      // Upload immediately to Supabase storage
      const imageUUID = generateImageUuid();
      const path = `${userId}/${imageUUID}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(path, file, { upsert: false });

      if (uploadError) {
        console.error('[ProfileAttachmentMenu] Upload error:', uploadError);
        return;
      }

      // Create metadata
      const metadata = {
        imageUUID,
        source: type === 'camera' ? 'camera' : 'upload',
        userId,
        bucket: 'profile-images',
        loading: false
      };

      // Call parent callback with the metadata
      onImageUploaded?.(metadata);
      onClose();
    } catch (error) {
      console.error('[ProfileAttachmentMenu] Error:', error);
    }
  };

  const buttons = [
    {
      icon: <Image className="h-8 w-8" />,
      label: 'Picture',
      onClick: handlePictureClick,
    },
    {
      icon: <Camera className="h-8 w-8" />,
      label: 'Camera',
      onClick: handleCameraClick,
    },
  ];

  return (
    <AttachmentMenu
      ref={attachmentMenuRef}
      buttons={buttons}
      onClose={onClose}
      onFileSelect={handleFileSelect}
      acceptMultiple={false}
    />
  );
});

ProfileAttachmentMenu.displayName = 'ProfileAttachmentMenu';

export default ProfileAttachmentMenu;
